/**
 * Message Action - WHAT Section
 *
 * Edit mode UI for configuring a one-time message automation.
 * The Message action is primarily trigger-driven with minimal "what" config.
 * The main configuration is in the Message section (content), not What.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-message.hbs
 *
 * Special case: low-stock-threshold trigger shows product category + location config
 *
 * Fields:
 * - reminderTypeId: Product categories (only for low-stock-threshold)
 * - locationId: Location filter (only for low-stock-threshold)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { fetchLocations } from 'src/models/location/location';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Message WHAT Section - Edit mode
 *
 * Most Message automations don't have a What section - the message itself
 * is configured in the Message section. Only low-stock-threshold has What config.
 */
export const MessageWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const { session } = useSession();

  // Fetch locations for low-stock-threshold
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations(),
    staleTime: 60000,
  });

  const locations = locationsData?.locations || [];
  const productTags = session?.productTags || [];

  const selectedCategories = Array.isArray(automation.reminderTypeId)
    ? automation.reminderTypeId
    : [];

  // Only show what config for low-stock-threshold trigger
  if (automation.reminderType !== 'low-stock-threshold') {
    return (
      <Typography color="text.secondary" fontStyle="italic">
        {t('automations.message.configureBelow')}
      </Typography>
    );
  }


  return (
    <Stack spacing={2}>
      {/* Product Categories */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.message.productCategories')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <Autocomplete
            multiple
            options={productTags}
            value={selectedCategories}
            onChange={(_, newValue) => {
              setFieldValue(`automations.${index}.reminderTypeId`, newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('automations.common.any')} size="small" />
            )}
            size="small"
          />
        </Box>
      </Box>

      {/* Location */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.message.location')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <Autocomplete
            options={locations}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            value={
              locations.find(
                (l) => l.id.toString() === automation.locationId?.toString()
              ) || null
            }
            onChange={(_, newValue) => {
              setFieldValue(
                `automations.${index}.locationId`,
                newValue?.id || null
              );
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('automations.message.anyLocation')} size="small" />
            )}
            size="small"
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Message What Summary Component
 */
export const MessageWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  // Fetch locations for display
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations(),
    staleTime: 60000,
  });

  const locations = locationsData?.locations || [];
  const location = locations.find(
    (l) => l.id.toString() === automation.locationId?.toString()
  );

  const categories = Array.isArray(automation.reminderTypeId)
    ? automation.reminderTypeId
    : [];

  const locationText = location ? `${location.name} location` : t('automations.message.anyLocation');
  const categoryText =
    categories.length > 0
      ? t(categories.length === 1 ? 'automations.message.withCategory' : 'automations.message.withCategories', { categories: categories.join(', ') })
      : t('automations.message.withAnyCategory');

  // Only low-stock-threshold has a real summary
  if (automation.reminderType !== 'low-stock-threshold') {
    return <>{t('automations.message.sendWhenTriggered')}</>;
  }

  return (
    <>
      {locationText} {categoryText}
    </>
  );
};
